import { useEffect } from 'react';
import { Phone, Calendar, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import type { MarketingPage } from '@/data/marketingPages';
import { pageDates } from '@/data/pageDates';

const SITE = 'https://www.morganlegalny.com';

interface Props {
  page: MarketingPage;
  /** Дополнительный блок под текстом страницы, перед призывом к действию. */
  after?: React.ReactNode;
}

function buildSchema(p: MarketingPage) {
  const canonical = `${SITE}${p.url}`;
  const dates = pageDates(p.url);
  const type = p.schemaType ?? 'WebPage';

  return [
    {
      '@context': 'https://schema.org',
      '@type': type,
      url: canonical,
      name: p.metaTitle,
      description: p.metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      datePublished: dates.published,
      dateModified: dates.modified,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.lead', '[data-speakable]'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: p.heroHeadline, item: canonical },
      ],
    },
  ];
}

function HeroBanner({ p }: { p: MarketingPage }) {
  return (
    <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span
          className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
          style={{ opacity: 0.03 }}
        >
          {p.heroHeadline.toUpperCase()}
        </span>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
          <a href="/" className="hover:text-gold transition-colors">Home</a>
          <span className="mx-2 text-gold/60">/</span>
          <span className="text-white/80">{p.heroHeadline}</span>
        </nav>

        <div className="max-w-3xl">
          <div className="section-label mb-5 text-gold">{p.heroEyebrow}</div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-7">
            {p.heroHeadline}
            {p.heroHeadlineAccent ? (
              <>
                {' '}
                <span className="text-gold italic font-display">{p.heroHeadlineAccent}</span>
              </>
            ) : null}
          </h1>
          <p className="lead font-body text-white/80 text-lg lg:text-xl leading-relaxed mb-9 max-w-2xl" data-speakable>
            {p.heroLead}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="/appointment/" className="btn-gold">
              <Calendar className="w-4 h-4" />
              Schedule Free Consultation
            </a>
            <a
              href="tel:8885291315"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-gold hover:text-navy"
            >
              <Phone className="w-4 h-4" />
              888-529-1315
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Sections({ p }: { p: MarketingPage }) {
  const { ref, visible } = useScrollReveal(0.1);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 lg:space-y-16">
        {p.sections.map((sec, i) => (
          <article
            key={i}
            className={`grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-10 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            }`}
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            {sec.heading ? (
              <header className="md:col-span-4">
                <div className="section-label mb-3">Section {i + 1}</div>
                <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight">{sec.heading}</h2>
              </header>
            ) : null}

            <div className={sec.heading ? 'md:col-span-8 space-y-4' : 'md:col-span-12 space-y-4'}>
              {sec.intro ? (
                <p className="font-body text-navy/80 text-base lg:text-lg leading-relaxed font-medium">{sec.intro}</p>
              ) : null}
              {sec.paragraphs?.map((para, j) => (
                <p key={`p-${j}`} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed">
                  {para}
                </p>
              ))}
              {sec.bullets ? (
                <ul className="space-y-3 pt-2">
                  {sec.bullets.map((b, k) => (
                    <li key={`b-${k}`} className="flex items-start gap-3 font-body text-navy/75 text-base leading-relaxed">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-1.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function CtaBand({ p }: { p: MarketingPage }) {
  if (!p.cta) return null;
  return (
    <section className="bg-gold py-16 lg:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-navy text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
          {p.cta.headline}
        </h2>
        <p className="font-body text-navy/85 text-base lg:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
          {p.cta.body}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="/appointment/" className="btn-navy">
            <Calendar className="w-4 h-4" />
            Schedule Free Consultation
          </a>
          <a
            href="tel:8885291315"
            className="inline-flex items-center gap-2 border-2 border-navy text-navy font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-navy hover:text-gold"
          >
            <Phone className="w-4 h-4" />
            888-529-1315
          </a>
        </div>
      </div>
    </section>
  );
}

function setHeadTag(selector: string, attr: 'content' | 'href', value: string) {
  const el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (el) (el as unknown as Record<string, string>)[attr] = value;
}

function useHead(p: MarketingPage) {
  useEffect(() => {
    const canonical = `${SITE}${p.url}`;
    if (typeof document !== 'undefined') {
      document.title = p.metaTitle;
      setHeadTag('meta[name="description"]', 'content', p.metaDescription);
      setHeadTag('link[rel="canonical"]', 'href', canonical);
      setHeadTag('meta[property="og:title"]', 'content', p.metaTitle);
      setHeadTag('meta[property="og:description"]', 'content', p.metaDescription);
      setHeadTag('meta[property="og:url"]', 'content', canonical);
      setHeadTag('meta[name="twitter:title"]', 'content', p.metaTitle);
      setHeadTag('meta[name="twitter:description"]', 'content', p.metaDescription);
    }
  }, [p]);
}

export default function ContentPageTemplate({ page, after }: Props) {
  const schema = buildSchema(page);
  useHead(page);
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main id="content-page">
        <HeroBanner p={page} />
        <Sections p={page} />
        {after}
        <CtaBand p={page} />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
